import { useCallback, useState } from 'react';
import { ProspectHistorySection } from '../profiles/ProspectHistorySection';
import { JourneyOperationDialog } from './JourneyOperationDialog';
import type { JourneyIntent } from './journeyOperations';
import { useJourneyAdmin } from './useJourneyAdmin';
import { useProspectingAvailability } from './useProspectingAvailability';
import { useDeleteLead, useRemoveMembership } from './hooks';
import { copyMessageLabel, copyPickerGroups, ordinaryCopyChoices } from './copyMessages';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  InlineError,
  Input,
  SaveHint,
  Select,
  useAutoSave,
  useDebouncedAutoSave,
} from '../../components/ui';
import { useTapPeople } from '../projects/hooks';
import { useCanSendAsMailbox, useSendAsMailbox } from '../gmail/useMailbox';
import { EmailBodyEditor } from './EmailBodyEditor';
import { useAuth } from '../auth/AuthProvider';
import { usePhotoPicker } from './usePhotoPicker';
import { resolvePhotos } from '../gmail/emailPhotos';
import '../gmail/sendPreview.css';
import { KeyValueDate, KeyValueText } from './KeyValueRows';
import { ProfileShell } from '../profiles/ProfileShell';
import { ProfileIdentity, ProfileLinkRow } from '../profiles/ProfileIdentity';
import { PropertyCard, PropertyControl } from '../profiles/ProfileProperty';
import { LeadActivityTab } from './LeadActivityTab';
import { LeadMailThreads } from './LeadMailThreads';
import { LeadRoundBlock } from './LeadRoundBlock';
import { NextActionBlock } from './NextActionBlock';
import { nurtureNoteArgs, nurtureToggleArgs } from './nurtureNote';
import {
  readCollapsedLeadSections,
  toggleLeadSection,
  writeCollapsedLeadSections,
} from './leadPanelSections';
import { CaptureMethodField, CategoryField, NurturePanel, OriginField } from './LeadFieldInputs';
import { JourneyStrip, JourneyCounts } from './JourneyStrip';
import { StartedOnLinkedIn } from './StartedOnLinkedIn';
import { useLeadJourney, useLeadJourneyDeals } from './linkedinJourney';
import { DealLinkList } from './DealLinkList';
import { useDealsForLead } from './leadDealLinks';
import {
  dealErrorMessage,
  dealOutcomeLabel,
  dealOutcomeTone,
  useLeadDealOutcomes,
  usePromoteLeadToDeal,
} from './dealOutcomes';
import { selectableStages } from './convertedGuard';
import {
  useAddLeadsToList,
  useComposeDraft,
  useCopySets,
  useLeadLists,
  useUpdateLead,
  useUpdateMembership,
} from './hooks';
import { activityErrorMessage, lastTouchLabel, useLogLeadActivity } from './leadActivity';
import {
  categoriesOf,
  leadFieldErrorMessage,
  nameOf,
  offerable,
  useCategoryLinks,
  useClosureReasons,
  useCorrectLeadDirection,
  useLeadCaptureMethods,
  useLeadCategories,
  useLeadSources,
  useSetLeadCategories,
  useSetNurture,
} from './leadFields';
import { blocksContact } from './leadListSafety';
import { leadStageKey, stageByKey } from './stageKeys';
import { composeErrorMessage, dayLabelWithYear, shortCategory, todayIso } from './trackerFormat';
import { holdLabel, isLeadHeld } from './leadHold';
import {
  SEQ_LABELS,
  SEQ_TONES,
  SOURCE_LABELS,
  copyEmail,
  copySetIndex,
  copySetTouches,
  gridTouches,
  leadName,
  membershipTouches,
  replaceTokens,
  salesErrorMessage,
  setOfMembership,
  touchOf,
} from './types';
import type { Lead, LeadStage, Membership, Touch } from './types';
import type { StageKey } from './stageKeys';

/**
 * What each status means, at the point where somebody picks one.
 *
 * From the review of the handbook: "A good internal tool should not need this
 * much reading for basic actions like 'what status do I use?'" — so the answer
 * moves out of the document and under the field. One line each, written as the
 * test somebody can apply to the lead in front of them.
 */
const STATUS_MEANINGS: Record<StageKey, string> = {
  new: 'No outreach yet — nobody has contacted them.',
  contacted: 'We sent something. No reply yet.',
  followup: 'We sent something and somebody is still on it — a next step is set.',
  engaged: 'They replied and the conversation is live.',
  nurture: 'Alive and interested, but not now. Parked on purpose, not forgotten.',
  converted: 'There is a real opportunity. It is tracked in Deals.',
  closed: 'We have stopped pursuing this lead. Needs a reason.',
};

/**
 * The middle column's tabs.
 *
 * 'details' is gone. Everything it held is the record column now: properties
 * on the left, associated records on the right, both visible without choosing
 * to look at them. A tab you had to select to see who the lead is was the
 * thing this relayout existed to remove.
 */
type PanelTab = 'activity' | 'emails' | 'outreach' | 'compose';

export function LeadPanel({
  lead,
  stages,
  allMemberships,
  onClose,
  onOpenCompany,
}: {
  lead: Lead;
  stages: LeadStage[];
  allMemberships: Membership[];
  onClose: () => void;
  /**
   * Jump to the company profile from the header — the other half of the loop
   * the company drawer's lead chips already close. Absent when there is
   * nowhere to jump to. Only offered when the lead is LINKED to a company
   * record: a bare company_name with no company_id has no profile to open,
   * and a link that goes nowhere teaches people links here are decoration.
   */
  onOpenCompany?: ((companyId: string) => void) | undefined;
}) {
  const update = useUpdateLead();
  const isAdmin = useJourneyAdmin();
  const { operations_enabled } = useProspectingAvailability();
  const remove = useDeleteLead();
  const removeFromList = useRemoveMembership();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [panelTab, setPanelTab] = useState<PanelTab>('activity');
  const [error, setError] = useState('');
  const [cleanup, setCleanup] = useState<JourneyIntent[] | null>(null);
  // Lists the lead is not on stay behind one line until asked for.
  const [showAllLists, setShowAllLists] = useState(false);
  // Which governance sections this person has folded. Per SECTION, never per
  // lead — folding Lead Origin once should not have to be repeated on every
  // lead opened, which is what keying this on lead.id would have caused.
  const [collapsedSections, setCollapsedSections] = useState<string[]>(() =>
    readCollapsedLeadSections(),
  );
  const isSectionCollapsed = (key: string) => collapsedSections.includes(key);
  const toggleSectionCollapsed = (key: string) => {
    setCollapsedSections((current) => {
      const next = toggleLeadSection(current, key);
      writeCollapsedLeadSections(next);
      return next;
    });
  };
  // Batch 1b fields on the lead's own panel.
  const panelCategories = useLeadCategories();
  const panelLinks = useCategoryLinks();
  const setLeadCategories = useSetLeadCategories();
  const setNurture = useSetNurture();
  const correctDirection = useCorrectLeadDirection();
  const [nurtureNote, setNurtureNote] = useState(lead.nurture_note);
  const [correcting, setCorrecting] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  // Batch 2. Closing needs a reason, and until now there was no way to give
  // one: the database refused every attempt and the panel had nowhere to
  // answer it. Held in state rather than written on change, because the two
  // values have to reach the database in the same update or the guard fires.
  const panelReasons = useClosureReasons();
  const [closing, setClosing] = useState(false);
  const [closingReasonId, setClosingReasonId] = useState<string | null>(null);
  const panelSources = useLeadSources();
  const captureMethods = useLeadCaptureMethods();
  const panelPeople = useTapPeople(true);
  const actorNames = new Map(
    (panelPeople.data ?? []).map((person) => [
      person.id,
      person.display_name === '' ? person.email : person.display_name,
    ]),
  );
  const closedStage = stageByKey(stages, 'closed');
  const currentStageKey = leadStageKey(lead.stage_id, stages);
  // Batch 3a. Won is not a lead status — it is an outcome on a deal — so the
  // way to say "this became something" is to raise the deal.
  const promote = usePromoteLeadToDeal();
  const panelOutcomes = useLeadDealOutcomes();
  const myOutcome = panelOutcomes.data?.get(lead.id);
  // The actual deals, not just the rollup — the section lists them as rows
  // somebody can open. Same RLS as the rollup, so they agree on visibility.
  const myDeals = useDealsForLead(lead.id);
  const journey = useLeadJourney(lead.id);
  const journeyDeals = useLeadJourneyDeals(
    lead.id,
    Boolean(journey.data?.prospect_id),
    myDeals.dataUpdatedAt,
  );
  const [promoting, setPromoting] = useState(false);
  const [promotePipeline, setPromotePipeline] = useState<'cg_deals' | 'partnerships'>('cg_deals');
  const [promoteName, setPromoteName] = useState('');

  const leadMemberships = allMemberships.filter((m) => m.lead_id === lead.id);
  // Drop 139: the membership table's column count follows the longest campaign
  // this lead is on, so a seven-email sequence is not truncated to four here.
  // Same cached query OutreachView uses, so this costs no extra round trip.
  const panelSets = copySetIndex(useCopySets().data ?? []);
  const panelTouches = gridTouches(leadMemberships, panelSets);
  const listsQuery = useLeadLists();
  const lists = listsQuery.data ?? [];
  const listName = (listId: string) => lists.find((l) => l.id === listId)?.name ?? listId;
  const addToList = useAddLeadsToList();
  // null while the vocabulary is loading: say nothing rather than guess, and
  // let the database trigger be what actually refuses. See leadListSafety.ts.
  const leadBlocksContact = blocksContact(lead, panelReasons.data ?? []);
  const updateMembershipMutation = useUpdateMembership(null);
  const membershipByList = new Map(leadMemberships.map((m) => [m.list_id, m]));

  function report(cause: unknown) {
    setError(salesErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function toggleList(listId: string) {
    setError('');
    const membership = membershipByList.get(listId);
    if (!operations_enabled && cleanup !== null) return;
    if (membership === undefined) {
      addToList.mutate({ listId, leadIds: [lead.id] }, { onError: report });
    } else if (!operations_enabled) {
      removeFromList.mutate(membership.id, { onError: report });
    } else {
      setCleanup([
        {
          kind: 'cleanup',
          table: 'lead_list_memberships',
          id: membership.id,
          label: `Remove ${leadName(lead)} from ${listName(listId)}`,
        },
      ]);
    }
  }

  /** One writer for every contact field; each row saves itself on blur. */
  const commit = useCallback(
    (changes: Record<string, string | null>) =>
      new Promise((resolve, reject) => {
        setError('');
        update.mutate(
          { leadId: lead.id, changes },
          {
            onSuccess: resolve,
            onError: (cause) => {
              setError(salesErrorMessage(cause.message));
              reject(cause);
            },
          },
        );
      }),
    [lead.id, update],
  );

  /**
   * Status saves itself and says so, like every text row in the drawer.
   *
   * It always saved on change; what it never did was tell you. Four of the
   * drawer's fields announced their save and three did not, which is half of
   * "how do I change this, and did it take?" having different answers in one
   * panel.
   */
  const statusSave = useAutoSave<string>(
    useCallback((stageId: string) => commit({ stage_id: stageId }), [commit]),
  );

  /** Same, for the origin select. */
  const captureSave = useAutoSave<string>(
    useCallback(
      (methodId: string) =>
        new Promise((resolve, reject) => {
          setError('');
          update.mutate(
            // '' clears it, exactly as the origin field does: "Not said" is a
            // real answer somebody can give, not an absence of one.
            { leadId: lead.id, changes: { capture_method_id: methodId } },
            {
              onSuccess: resolve,
              onError: (cause) => {
                setError(leadFieldErrorMessage(cause.message));
                reject(cause instanceof Error ? cause : new Error(String(cause)));
              },
            },
          );
        }),
      [lead.id, update],
    ),
  );
  const originSave = useAutoSave<string>(
    useCallback(
      (originId: string) =>
        new Promise((resolve, reject) => {
          setError('');
          update.mutate(
            // '' clears it. The select's "Not said" option is a real answer —
            // somebody removing a wrong origin — so it must reach the database
            // rather than being read as "nothing to save".
            { leadId: lead.id, changes: { lead_source_id: originId } },
            {
              onSuccess: resolve,
              onError: (cause) => {
                setError(leadFieldErrorMessage(cause.message));
                reject(cause);
              },
            },
          );
        }),
      [lead.id, update],
    ),
  );

  /**
   * The nurture note saves itself, and saving it does NOT end the nurture.
   *
   * Before this there was no path that did both. The textarea only rendered
   * while the lead was in nurture, onNoteChange wrote to local state and
   * nothing else, and the single call to setNurture.mutate lived inside the
   * toggle — which, while active, reads "Take out of nurture" and sends
   * active: false. So closing the drawer lost the words silently, and the one
   * button on screen saved them by doing the thing nobody meant to do.
   *
   * `active: true` is passed explicitly rather than read from a toggle: this
   * writer's whole job is to leave that alone.
   */
  const nurtureNoteSave = useDebouncedAutoSave<string>(
    useCallback(
      (next: string) =>
        new Promise((resolve, reject) => {
          setNurture.mutate(nurtureNoteArgs(lead.id, next), {
            onSuccess: resolve,
            onError: reject,
          });
        }),
      [lead.id, setNurture],
    ),
  );

  function setSource(source: 'inbound' | 'outbound') {
    setError('');
    update.mutate({ leadId: lead.id, changes: { source } }, { onError: report });
  }

  return (
    <Drawer
      open
      // `wide`, not `half`, and the difference is structural rather than
      // cosmetic. `half` caps at 1040px, which leaves the profile shell 1000px
      // after the drawer body's padding, under the 1060px three-column floor
      // in profileLayout.css. A lead could therefore never show three columns
      // on any monitor, while the company and deal drawers beside it could.
      //
      // `half` was Kyu's own request on 3 September, "it's better if it takes
      // 50% of the screen real estate". His 14 September ruling for the
      // interconnected profiles replaced it with 75%; company and deal moved,
      // this one did not. Kyu, seeing a lead beside a deal: "these looks like
      // shit."
      wide
      title={leadName(lead)}
      onClose={onClose}
      menuItems={
        !operations_enabled && cleanup === null
          ? [
              {
                label: confirmingDelete ? 'Really delete lead' : 'Delete lead',
                danger: true,
                onSelect: () => {
                  if (!confirmingDelete) {
                    setConfirmingDelete(true);
                    return;
                  }
                  setError('');
                  remove.mutate(lead.id, { onSuccess: onClose, onError: report });
                },
              },
            ]
          : operations_enabled && isAdmin
            ? [
                {
                  label: 'Delete lead',
                  danger: true,
                  onSelect: () => {
                    setCleanup([
                      { kind: 'cleanup', table: 'leads', id: lead.id, label: leadName(lead) },
                    ]);
                  },
                },
              ]
            : []
      }
    >
      <div className="lead-panel-inner">
        {error !== '' && <InlineError>{error}</InlineError>}

        {journey.isError && <InlineError>The LinkedIn journey could not be loaded.</InlineError>}
        <JourneyStrip
          row={journey.data}
          deals={journeyDeals.isPending || journeyDeals.isError ? undefined : journeyDeals.data}
        />

        <ProfileShell
          identity={
            // No name prop: the drawer header two rows up already prints it and
            // is this drawer's aria-labelledby. A page mount passes one.
            <ProfileIdentity
              role={
                <>
                  {lead.title === '' ? null : lead.title}
                  {lead.title === '' || lead.company_name === '' ? null : ' at '}
                  {/* The company name is a door when a company RECORD is linked,
                      and plain text when only a name string exists. A link that
                      opens nothing teaches people the links here are decoration. */}
                  {lead.company_name === '' ? null : lead.company_id !== null &&
                    onOpenCompany !== undefined ? (
                    <button
                      type="button"
                      className="tracker-lead-link"
                      title="Open the company profile"
                      onClick={() => {
                        if (lead.company_id !== null) {
                          onOpenCompany(lead.company_id);
                        }
                      }}
                    >
                      {lead.company_name}
                    </button>
                  ) : (
                    lead.company_name
                  )}
                </>
              }
            >
              {/* Email and phone are the two things anybody takes off a record,
                  so they sit at the top with a copy button rather than seven
                  scrolls down inside a form. They stay editable in About. */}
              {lead.email === '' ? null : (
                <ProfileLinkRow label="email" value={lead.email} href={`mailto:${lead.email}`} />
              )}
              {lead.phone === '' ? null : (
                <ProfileLinkRow label="phone" value={lead.phone} href={`tel:${lead.phone}`} />
              )}
              <p className="lead-panel-standing">
                <Badge tone="neutral">
                  {stages.find((s) => s.id === lead.stage_id)?.name ?? 'No status'}
                </Badge>
                <span className="lead-panel-standing-facts">
                  {[
                    lead.source === null ? 'Source not set' : SOURCE_LABELS[lead.source],
                    ...categoriesOf(lead.id, panelLinks.data ?? []).map((id) =>
                      shortCategory(nameOf(id, panelCategories.data ?? [])),
                    ),
                    lead.last_touch_at === null
                      ? 'never touched'
                      : `last touch ${lastTouchLabel(lead.last_touch_at, todayIso()).toLowerCase()}`,
                  ].join(' · ')}
                </span>
                {!lead.nurture_active ? null : (
                  <Badge tone="neutral" micro>
                    Nurture
                  </Badge>
                )}
                {!lead.bad_contact ? null : (
                  <Badge tone="warning" micro>
                    Bad contact
                  </Badge>
                )}
              </p>
              {!operations_enabled && confirmingDelete && (
                <p className="company-panel-confirm">
                  Choose Delete lead again to go ahead.{' '}
                  <button
                    type="button"
                    className="tracker-lead-link"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Keep it
                  </button>
                </p>
              )}
            </ProfileIdentity>
          }
          properties={
            <>
              {/* Status first and open by default. Where this lead stands and
                  what happens next were three separate strips; they are one
                  question and now one card. */}
              <PropertyCard
                title="Status"
                icon="clock"
                tone="purple"
                collapsed={isSectionCollapsed('status')}
                onToggle={() => {
                  toggleSectionCollapsed('status');
                }}
              >
                <Select
                  label="Status"
                  labelHidden
                  hint={
                    <>
                      {STATUS_MEANINGS[currentStageKey ?? 'new']}{' '}
                      <SaveHint state={statusSave.state} error={statusSave.error} />
                    </>
                  }
                  value={closing && closedStage !== null ? closedStage.id : lead.stage_id}
                  // Converted to Deal is not offered here — it is what raising
                  // a deal does. Six of nine converted leads had no deal
                  // because this dropdown offered it. See convertedGuard.ts.
                  options={selectableStages(stages, lead.stage_id).map((stage) => ({
                    value: stage.id,
                    label: stage.name,
                  }))}
                  onChange={(event) => {
                    setError('');
                    // Closing is the one move that needs a second answer, so it
                    // waits for the reason instead of firing an update the
                    // database will refuse.
                    if (closedStage !== null && event.target.value === closedStage.id) {
                      setClosing(true);
                      return;
                    }
                    setClosing(false);
                    setClosingReasonId(null);
                    statusSave.save(event.target.value);
                  }}
                />
                {!closing || closedStage === null ? null : (
                  <div className="tracker-field-block">
                    <Select
                      label="Why is this closed?"
                      hint="Saved on the lead. It is what the Reports funnel counts, so pick the true one."
                      value={closingReasonId ?? ''}
                      onChange={(event) =>
                        setClosingReasonId(event.target.value === '' ? null : event.target.value)
                      }
                      options={[
                        { value: '', label: 'Pick a reason' },
                        ...offerable(panelReasons.data ?? []).map((reason) => ({
                          value: reason.id,
                          label: reason.name,
                        })),
                      ]}
                    />
                    <div className="lead-action-row">
                      <Button
                        size="sm"
                        disabled={closingReasonId === null || update.isPending}
                        onClick={() => {
                          setError('');
                          update.mutate(
                            {
                              leadId: lead.id,
                              changes: {
                                stage_id: closedStage.id,
                                closure_reason_id: closingReasonId,
                              },
                            },
                            {
                              onSuccess: () => {
                                setClosing(false);
                                setClosingReasonId(null);
                              },
                              onError: (cause) => setError(leadFieldErrorMessage(cause.message)),
                            },
                          );
                        }}
                      >
                        Close lead
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setClosing(false);
                          setClosingReasonId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                {currentStageKey !== 'closed' || lead.closure_reason_id === null ? null : (
                  <p className="tracker-field-note">
                    {/* The date reads beside the reason and nowhere else. It is a
                        filter input, not a thing to scan, so it earns no
                        fourteenth column on a table Kyu locked at thirteen.

                        "date not recorded" rather than a guess, on the leads
                        closed before 20261219000100 — nothing was backfilled,
                        and this is the same phrasing deals.closed_on already
                        uses for its legacy rows. */}
                    Closed{' '}
                    {lead.closed_on === null
                      ? '(date not recorded)'
                      : `on ${dayLabelWithYear(lead.closed_on)}`}
                    : {nameOf(lead.closure_reason_id, panelReasons.data ?? [])}. Move it to another
                    status to reopen it — the reason and the date are cleared then, and the timeline
                    keeps the history.
                  </p>
                )}
                <PropertyControl label="Next action">
                  <NextActionBlock recordId={lead.id} recordKind="lead" onError={report} />
                </PropertyControl>
                <PropertyControl label="Follow-up round">
                  <LeadRoundBlock lead={lead} onError={report} />
                </PropertyControl>
              </PropertyCard>

              <PropertyCard
                title="About this lead"
                icon="brief"
                tone="blue"
                collapsed={isSectionCollapsed('about')}
                onToggle={() => {
                  toggleSectionCollapsed('about');
                }}
              >
                <div className="kv-list">
                  <KeyValueText
                    label="First name"
                    value={lead.first_name}
                    onCommit={(next) => commit({ first_name: next.trim() })}
                  />
                  <KeyValueText
                    label="Last name"
                    value={lead.last_name}
                    onCommit={(next) => commit({ last_name: next.trim() })}
                  />
                  <KeyValueText
                    label="Email"
                    value={lead.email}
                    onCommit={(next) => commit({ email: next.trim() })}
                  />
                  <KeyValueText
                    label="Phone"
                    value={lead.phone}
                    placeholder="Not set"
                    onCommit={(next) => commit({ phone: next.trim() })}
                  />
                  <KeyValueText
                    label="Company"
                    value={lead.company_name}
                    placeholder="Not set"
                    onCommit={(next) => commit({ company_name: next.trim() })}
                  />
                  <KeyValueText
                    label="Title"
                    value={lead.title}
                    placeholder="Not set"
                    onCommit={(next) => commit({ title: next.trim() })}
                  />
                  <KeyValueText
                    label="Notes"
                    value={lead.notes}
                    multiline
                    debounce
                    placeholder="Nothing noted yet"
                    onCommit={(next) => commit({ notes: next })}
                  />
                  <KeyValueDate
                    label="Do not email until"
                    value={lead.email_hold_until}
                    onCommit={(next) => commit({ email_hold_until: next })}
                  />
                  <KeyValueText
                    label="Hold note"
                    value={lead.email_hold_note}
                    placeholder="Why should email wait?"
                    debounce
                    onCommit={(next) => commit({ email_hold_note: next })}
                  />
                </div>
              </PropertyCard>

              <PropertyCard
                title="Source and origin"
                icon="nurture"
                tone="grey"
                hint="Where this lead actually came from — the channel. Capture method is how their details reached us, which is not the same thing: a referral can arrive through a form."
                collapsed={isSectionCollapsed('source-origin')}
                onToggle={() => {
                  toggleSectionCollapsed('source-origin');
                }}
              >
                <PropertyControl label="Lead source (consent record)">
                  {lead.source === null ? (
                    <div className="lead-action-row lead-action-row--wide">
                      <Button variant="secondary" size="sm" onClick={() => setSource('inbound')}>
                        Mark Inbound
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setSource('outbound')}>
                        Mark Outbound
                      </Button>
                    </div>
                  ) : (
                    <p className="lead-source-stated">
                      <strong>{SOURCE_LABELS[lead.source]}</strong>
                      {lead.source === 'inbound' ? ' · opted in, the record is permanent' : ''}
                    </p>
                  )}
                  {/* The one door out of a mistake. Kyu: "Keep it permanent, but
                    let an admin correct it with a reason. That protects the
                    consent record while allowing genuine data-entry errors to
                    be fixed." Without this an import batch mistagged Inbound
                    claims consent forever. */}
                  {lead.source === null ? null : correcting ? (
                    <div className="tracker-field-block lead-action-row--top">
                      <Input
                        label="Why is this wrong?"
                        placeholder="e.g. Typeform import was mistagged"
                        value={correctionReason}
                        onChange={(event) => setCorrectionReason(event.target.value)}
                      />
                      <p className="tracker-field-note">
                        This is written to the lead’s history with your name. TAP admins only.
                      </p>
                      <div className="lead-action-row">
                        <Button
                          size="sm"
                          disabled={correctDirection.isPending || correctionReason.trim() === ''}
                          onClick={() => {
                            setError('');
                            correctDirection.mutate(
                              {
                                leadId: lead.id,
                                direction: lead.source === 'inbound' ? 'outbound' : 'inbound',
                                reason: correctionReason,
                              },
                              {
                                onSuccess: () => {
                                  setCorrecting(false);
                                  setCorrectionReason('');
                                },
                                onError: (cause) => setError(leadFieldErrorMessage(cause.message)),
                              },
                            );
                          }}
                        >
                          Correct to {lead.source === 'inbound' ? 'Outbound' : 'Inbound'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setCorrecting(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="lead-action-row lead-action-row--top">
                      <Button variant="ghost" size="sm" onClick={() => setCorrecting(true)}>
                        Correct this
                      </Button>
                    </div>
                  )}
                </PropertyControl>
                <div className="lead-panel-two-col">
                  <OriginField
                    labelHidden
                    value={lead.lead_source_id}
                    leadSource={lead.source}
                    sources={panelSources.data ?? []}
                    saveHint={<SaveHint state={originSave.state} error={originSave.error} />}
                    onChange={(next) => {
                      originSave.save(next ?? '');
                    }}
                  />
                  {/* Directly beneath, not in a section of its own. Where they
                    came from and how they reached us are read together, and a
                    twelfth drawer section holding one select would cost more
                    than the clutter it saved. */}
                  <CaptureMethodField
                    value={lead.capture_method_id}
                    leadSource={lead.source}
                    methods={captureMethods.data ?? []}
                    saveHint={<SaveHint state={captureSave.state} error={captureSave.error} />}
                    onChange={(next) => {
                      captureSave.save(next ?? '');
                    }}
                  />
                </div>
                <PropertyControl label="Contactable">
                  {!lead.bad_contact ? (
                    <div className="lead-panel-row">
                      <p className="tracker-field-note">No delivery problem recorded.</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => {
                          setError('');
                          update.mutate(
                            { leadId: lead.id, changes: { bad_contact: true } },
                            { onError: report },
                          );
                        }}
                      >
                        Mark address as bad
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="tracker-field-note">
                        <Badge tone="warning">Bad contact</Badge> Email to this address does not
                        arrive. Find a better one, or close the lead as Invalid contact.
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => {
                          setError('');
                          update.mutate(
                            { leadId: lead.id, changes: { bad_contact: false } },
                            { onError: report },
                          );
                        }}
                      >
                        Address works again
                      </Button>
                    </>
                  )}
                </PropertyControl>
              </PropertyCard>

              <PropertyCard
                title="Categories"
                icon="filter"
                tone="grey"
                hint="What this lead is for. A lead can be both."
                collapsed={isSectionCollapsed('categories')}
                onToggle={() => {
                  toggleSectionCollapsed('categories');
                }}
              >
                <CategoryField
                  selected={categoriesOf(lead.id, panelLinks.data ?? [])}
                  categories={panelCategories.data ?? []}
                  labelHidden
                  onChange={(next) => {
                    setError('');
                    setLeadCategories.mutate(
                      { leadId: lead.id, categoryIds: next },
                      { onError: report },
                    );
                  }}
                />
                <PropertyControl
                  label="Nurture"
                  explain="A park, not a status: a lead stays New, Contacted or Engaged while in nurture. It warns before direct sales follow-ups. It does not pause any task or stop anybody emailing."
                >
                  <NurturePanel
                    labelHidden
                    active={lead.nurture_active}
                    note={nurtureNote}
                    pending={setNurture.isPending}
                    noteSave={nurtureNoteSave}
                    onNoteBlur={nurtureNoteSave.flush}
                    onNoteChange={(next) => {
                      setNurtureNote(next);
                      nurtureNoteSave.save(next);
                    }}
                    onToggle={(next) => {
                      setError('');
                      setNurture.mutate(nurtureToggleArgs(lead.id, next, nurtureNote), {
                        onError: report,
                      });
                    }}
                  />
                </PropertyControl>
              </PropertyCard>
            </>
          }
          associations={
            <>
              <StartedOnLinkedIn
                row={journey.data}
                leadId={lead.id}
                collapsed={isSectionCollapsed('linkedin-start')}
                onToggle={() => toggleSectionCollapsed('linkedin-start')}
              />
              {journey.data?.prospect_id && journeyDeals.data && (
                <JourneyCounts deals={journeyDeals.data ?? []} />
              )}
              {journey.data?.prospect_id && journeyDeals.isError && (
                <InlineError>Linked deals could not be loaded.</InlineError>
              )}
              {/* The company comes FIRST, above the deals.
                  Kyu, 15 September: "when you open a lead, it should show the
                  company and the deal... All of those three should always be
                  seen right away."
                  It existed only as a field with a small Open link beside it,
                  which is a different promise: a field says what the value is,
                  an association says there is another record here and takes
                  you to it. The company is the record a lead most often needs
                  next, so it leads the column. */}
              <PropertyCard
                title="Company"
                icon="workspace"
                tone="green"
                count={lead.company_id === null ? 0 : 1}
                collapsed={isSectionCollapsed('company')}
                onToggle={() => {
                  toggleSectionCollapsed('company');
                }}
              >
                {lead.company_name === '' ? (
                  <p className="tracker-field-note">No company on this lead yet.</p>
                ) : lead.company_id !== null && onOpenCompany !== undefined ? (
                  <button
                    type="button"
                    className="tracker-lead-link"
                    onClick={() => {
                      if (lead.company_id !== null) {
                        onOpenCompany(lead.company_id);
                      }
                    }}
                  >
                    {lead.company_name}
                  </button>
                ) : (
                  // A typed company name with no company record behind it.
                  // Printed, not linked: a link that goes nowhere teaches
                  // people the links here are decoration.
                  <p className="tracker-field-note">
                    {lead.company_name} <span>· not linked to a company record</span>
                  </p>
                )}
              </PropertyCard>

              {/* A deal and a list are other records. They belong beside the
                  lead, not inside it, and the count goes in the heading because
                  that is the only question a folded card has to answer. */}
              <PropertyCard
                title="Deals"
                icon="deals"
                tone="purple"
                scrolls
                count={myDeals.data?.length}
                collapsed={isSectionCollapsed('deals')}
                onToggle={() => {
                  toggleSectionCollapsed('deals');
                }}
              >
                {/* The deals themselves, as rows that open the deal — not a
                    rollup badge and a sentence pointing at another tab. Kyu's
                    HubSpot check: "the lead profile will have... whatever deal
                    is associated." The summary badge stays only while more
                    than one deal needs a one-line answer. */}
                {myOutcome !== undefined && (
                  <p className="tracker-field-note">
                    <Badge tone={dealOutcomeTone(myOutcome) ?? 'neutral'}>
                      {dealOutcomeLabel(myOutcome)}
                    </Badge>
                  </p>
                )}
                <DealLinkList
                  deals={myDeals.data}
                  isPending={myDeals.isPending}
                  isError={myDeals.isError}
                  emptyText="No deal raised from this lead yet."
                />
                {promoting ? (
                  <div className="tracker-field-block">
                    <Select
                      label="Which pipeline"
                      value={promotePipeline}
                      onChange={(event) =>
                        setPromotePipeline(event.target.value as 'cg_deals' | 'partnerships')
                      }
                      options={[
                        { value: 'cg_deals', label: 'CG Deals' },
                        { value: 'partnerships', label: 'Partnerships' },
                      ]}
                    />
                    <Input
                      label="Deal name"
                      placeholder={lead.company_name === '' ? leadName(lead) : lead.company_name}
                      value={promoteName}
                      onChange={(event) => setPromoteName(event.target.value)}
                    />
                    <p className="tracker-field-note">
                      Leave the name blank to use the company. The lead moves to Converted to Deal
                      and the promotion is recorded on its Activity — it does not count as a touch.
                    </p>
                    <div className="lead-action-row">
                      <Button
                        size="sm"
                        disabled={promote.isPending}
                        onClick={() => {
                          setError('');
                          promote.mutate(
                            { leadId: lead.id, pipeline: promotePipeline, name: promoteName },
                            {
                              onSuccess: () => {
                                setPromoting(false);
                                setPromoteName('');
                              },
                              onError: (cause) => setError(dealErrorMessage(cause.message)),
                            },
                          );
                        }}
                      >
                        {promote.isPending ? 'Raising…' : 'Raise the deal'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setPromoting(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="lead-action-row lead-action-row--top">
                    <Button variant="secondary" size="sm" onClick={() => setPromoting(true)}>
                      Promote to Deal
                    </Button>
                    {myOutcome === undefined ||
                    myOutcome.open_deals + myOutcome.won_deals === 0 ? null : (
                      <p className="tracker-field-note">
                        This lead already has a deal. Raising another is fine — repeat business is
                        why the column counts them separately.
                      </p>
                    )}
                  </div>
                )}
              </PropertyCard>

              <PropertyCard
                title="Lists"
                icon="files"
                tone="grey"
                scrolls
                count={leadMemberships.length}
                collapsed={isSectionCollapsed('lists')}
                onToggle={() => {
                  toggleSectionCollapsed('lists');
                }}
              >
                {/* The lists this lead is ON come first, always shown. The
                    other nineteen sit behind one line: every lead rendered all
                    twenty checkboxes, and a wall of unticked boxes buried the
                    one or two that said something. */}
                {lists.length === 0 ? (
                  <p className="tracker-field-note">No lists yet.</p>
                ) : (
                  <div className="lead-list-picker">
                    {/* Said BEFORE the click, not reported after it.
                        20261220000100 makes the database refuse this write, so
                        without the notice the checkbox would tick, nothing
                        would happen, and the person would reasonably conclude
                        the app is broken. Removing them from a list they are
                        already on stays available — that direction is always
                        the right one. */}
                    {leadBlocksContact !== true ? null : (
                      <p className="tracker-field-note">
                        This lead is closed as Do not contact, so they cannot be added to a list.
                        You can still take them off one.
                      </p>
                    )}
                    {lists
                      .filter((list) => membershipByList.has(list.id))
                      .map((list) => (
                        <Checkbox
                          key={list.id}
                          label={list.type === '' ? list.name : `${list.name} · ${list.type}`}
                          checked
                          onChange={() => {
                            toggleList(list.id);
                          }}
                        />
                      ))}
                    {leadBlocksContact === true ||
                    lists.filter((list) => !membershipByList.has(list.id)).length === 0 ? null : (
                      <button
                        type="button"
                        className="section-toggle lead-list-more"
                        aria-expanded={showAllLists}
                        onClick={() => setShowAllLists((open) => !open)}
                      >
                        <span aria-hidden="true" className="section-toggle-caret">
                          {showAllLists ? '▾' : '▸'}
                        </span>
                        Add to another list ·{' '}
                        {lists.filter((list) => !membershipByList.has(list.id)).length} available
                      </button>
                    )}
                    {!showAllLists
                      ? null
                      : lists
                          .filter((list) => !membershipByList.has(list.id))
                          .map((list) => (
                            <Checkbox
                              key={list.id}
                              label={list.type === '' ? list.name : `${list.name} · ${list.type}`}
                              checked={false}
                              onChange={() => {
                                toggleList(list.id);
                              }}
                            />
                          ))}
                  </div>
                )}
              </PropertyCard>
            </>
          }
          timeline={
            <>
              <ProspectHistorySection subject={{ table: 'leads', id: lead.id }} />
              <div className="lead-panel-tabs">
                {/* Details is gone as a tab. What it held is the record column
                    now, visible without choosing to look at it. */}
                {(['activity', 'emails', 'outreach', 'compose'] as PanelTab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`lead-panel-tab${panelTab === t ? ' lead-panel-tab--active' : ''}`}
                    onClick={() => setPanelTab(t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              <div className="lead-panel-body">
                {panelTab === 'activity' && (
                  <LeadActivityTab lead={lead} actorNames={actorNames} todayIso={todayIso()} />
                )}

                {/* The conversation itself, read live from the sales mailbox. Its
              own tab: it sat under Activity as a sub-view and Kyu could not
              find it ("where is the Hubspot like email thread box?"). */}
                {panelTab === 'emails' && (
                  <LeadMailThreads lead={lead} onCompose={() => setPanelTab('compose')} />
                )}

                {panelTab === 'outreach' && (
                  <div>
                    <p className="lead-panel-section-title">Outreach history</p>
                    <p className="tracker-field-note">
                      List cadence. Ticking a box records the email on the lead’s Activity too, so
                      the two are one history — and unticking removes it again.
                    </p>
                    {leadMemberships.length === 0 ? (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        This lead hasn't been added to any outreach list yet.
                      </p>
                    ) : (
                      <table className="tracker-table" style={{ marginTop: 'var(--space-2)' }}>
                        <thead>
                          <tr>
                            <th>List</th>
                            {panelTouches.map((touch) => (
                              <th key={touch}>T{touch}</th>
                            ))}
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadMemberships.map((m) => (
                            <tr key={m.id}>
                              <td>{listName(m.list_id)}</td>
                              {panelTouches.map((touch) => {
                                const state = touchOf(m, touch);
                                const sent = state.sentOn !== null;
                                if (
                                  !membershipTouches(m, setOfMembership(m, panelSets)).includes(
                                    touch,
                                  )
                                ) {
                                  return (
                                    <td key={touch} className="tracker-touch-cell">
                                      <p
                                        className="tracker-touch-date"
                                        aria-label={`Touch ${touch} is not part of this campaign`}
                                      >
                                        &ndash;
                                      </p>
                                    </td>
                                  );
                                }
                                return (
                                  <td key={touch} className="tracker-touch-cell">
                                    <input
                                      type="checkbox"
                                      checked={sent}
                                      aria-label={`Touch ${touch} ${sent ? 'sent' : 'not sent'} on ${listName(m.list_id)}`}
                                      onChange={() => {
                                        setError('');
                                        updateMembershipMutation.mutate(
                                          {
                                            membershipId: m.id,
                                            changes: sent
                                              ? {
                                                  [`touch${touch}_status`]: '',
                                                  [`touch${touch}_sent_on`]: null,
                                                }
                                              : {
                                                  [`touch${touch}_status`]: 'Sent',
                                                  [`touch${touch}_sent_on`]: todayIso(),
                                                },
                                          },
                                          { onError: report },
                                        );
                                      }}
                                    />
                                    {sent && <p className="tracker-touch-date">{state.sentOn}</p>}
                                  </td>
                                );
                              })}
                              <td>
                                <Badge tone={SEQ_TONES[m.seq_status]}>
                                  {SEQ_LABELS[m.seq_status]}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {panelTab === 'compose' && <ComposeSectionPanel lead={lead} />}
              </div>
            </>
          }
        />
      </div>
      {operations_enabled && cleanup !== null && (
        <JourneyOperationDialog
          intents={cleanup}
          onClose={() => setCleanup(null)}
          onSaved={() => {
            const removedLead = cleanup.some(
              (intent) => intent.kind === 'cleanup' && intent.table === 'leads',
            );
            setCleanup(null);
            if (removedLead) onClose();
          }}
        />
      )}
    </Drawer>
  );
}

function ComposeSectionPanel({ lead }: { lead: Lead }) {
  const { message_enabled } = useProspectingAvailability();
  const copySetsQuery = useCopySets();
  const compose = useComposeDraft();
  const canSend = useCanSendAsMailbox();
  const send = useSendAsMailbox();
  const { client } = useAuth();
  // Photos in the body (drop B). Picked here, attached inline at send time.
  const photoPicker = usePhotoPicker();
  const [copySetId, setCopySetId] = useState('');
  const [touch, setTouch] = useState<Touch>(1);
  // The words. A copy set PREFILLS these; the person edits or replaces them.
  //
  // Kyu, 3 September: "so we can only send email if it's a 'copy'?" — no, and
  // the panel that only offered copy sets was inherited from the draft flow
  // without being questioned. A template is a starting point, not a cage.
  // Pick nothing and write from scratch; pick a set and change a line for this
  // lead; either way what is in these two fields is exactly what goes out.
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [message, setMessage] = useState('');
  // The draft and the record used to be two actions in two places, which is
  // why the second one kept being forgotten. Now the button that records it
  // appears where the person already is, the moment the draft exists.
  const [drafted, setDrafted] = useState(false);
  const logSent = useLogLeadActivity();

  const sets = copySetsQuery.data ?? [];
  const set = sets.find((c) => c.id === copySetId) ?? null;

  // Prefill from the chosen set and touch, tokens filled for THIS lead. Runs
  // when the choice changes, and only then — typing in the fields afterwards
  // is the person's, and a re-render must not put the template back.
  function prefill(nextSetId: string, nextTouch: Touch) {
    const chosen = sets.find((c) => c.id === nextSetId) ?? null;
    if (chosen === null) return;
    const email = copyEmail(chosen, nextTouch);
    setSubject(replaceTokens(email.subject, lead));
    setBody(replaceTokens(email.body, lead));
  }

  const ready = subject.trim() !== '' && body.trim() !== '';
  const held = isLeadHeld(lead, todayIso());

  function create() {
    if (lead.email === '') {
      setMessage('This lead has no email address.');
      return;
    }
    if (!ready) {
      setMessage('Write a subject and a body first, or pick a copy set.');
      return;
    }
    setMessage('');
    compose.mutate(
      { to: lead.email, subject, body },
      {
        onSuccess: () => {
          setDrafted(true);
          setMessage('Draft created. Check Drafts in Gmail.');
        },
        onError: (cause) => setMessage(composeErrorMessage(cause.message)),
      },
    );
  }

  // ── Send as Alex ──────────────────────────────────────────────────────────
  //
  // Only drawn for the three people on the sender list (useCanSendAsMailbox);
  // everyone else keeps the draft flow. The email goes out from the sales
  // mailbox with Alex's own Gmail signature, and is recorded on this lead's
  // timeline with THIS person's name — through the same rule the mail sync
  // uses, so it ticks the next T1-T4 box when there is exactly one campaign it
  // could belong to. docs/sales-inbox-design-note.md.
  function sendNow() {
    if (lead.email === '') {
      setMessage('This lead has no email address.');
      return;
    }
    if (!ready) {
      setMessage('Write a subject and a body first, or pick a copy set.');
      return;
    }
    setMessage('');
    // Photos named in the body become inline parts first; a copy set's Drive
    // photo is read here with this person's Drive access. A photo that cannot
    // be found stops the send with a sentence, before anything goes out.
    void resolvePhotos(body, photoPicker.photos, client)
      .then((resolved) => {
        send.mutate(
          {
            leadId: lead.id,
            subject,
            body: resolved.body,
            // Which template this started from, if any. "Email 2 · Cold B" on the
            // timeline is worth more than "email" — and a hand-written one says so.
            note:
              set === null
                ? 'Written by hand'
                : `Email ${String(touch)} · ${message_enabled ? copyMessageLabel(set) : set.name}`,
            ...(resolved.inlinePhotos.length > 0 ? { inlinePhotos: resolved.inlinePhotos } : {}),
          },
          {
            onSuccess: (result) => {
              const where = result.threaded
                ? 'as a reply in the existing conversation'
                : 'as a new email';
              const record =
                result.outcome === 'ticked'
                  ? `Ticked Email ${String(touch)} on the list.`
                  : result.outcome === 'logged'
                    ? 'Logged on the timeline.'
                    : result.outcome === 'already'
                      ? 'Already on the timeline.'
                      : 'Sent, but the timeline entry failed — tell Kyu.';
              const carried =
                resolved.inlinePhotos.length === 0
                  ? ''
                  : ` ${String(resolved.inlinePhotos.length)} photo${resolved.inlinePhotos.length === 1 ? '' : 's'} in the body.`;
              setMessage(`Sent ${where}.${carried} ${record}`);
              photoPicker.reset();
            },
            onError: (cause) => setMessage(cause.message),
          },
        );
      })
      .catch((cause: unknown) => {
        setMessage(cause instanceof Error ? cause.message : 'The photos could not be read.');
      });
  }

  const sender = canSend.data === true;

  return (
    <div>
      <p className="lead-panel-section-title">{sender ? 'Email this lead' : 'Compose in Gmail'}</p>
      <div className="lead-panel-field-group">
        {sets.length === 0 ? null : (
          <>
            <Select
              label="Start from a copy set"
              value={copySetId}
              options={[
                { value: '', label: 'Write from scratch' },
                ...(message_enabled ? ordinaryCopyChoices(sets) : sets).map((c) => ({
                  value: c.id,
                  label: message_enabled ? copyMessageLabel(c) : c.name,
                })),
              ]}
              groups={message_enabled ? copyPickerGroups(sets, true) : []}
              onChange={(e) => {
                setCopySetId(e.target.value);
                prefill(e.target.value, touch);
              }}
            />
            {set === null ? null : (
              <Select
                label="Touch"
                value={String(touch)}
                options={copySetTouches(set).map((v) => ({
                  value: String(v),
                  label: `Email ${v}`,
                }))}
                onChange={(e) => {
                  const next = Number(e.target.value) as Touch;
                  setTouch(next);
                  prefill(copySetId, next);
                }}
              />
            )}
          </>
        )}

        {/* The email. Editable whether it came from a set or not: what is in
            these two fields is what goes out, and a preview that could not be
            changed was a template with a Send button on it. */}
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
        />
        <EmailBodyEditor
          label="Email"
          value={body}
          onChange={setBody}
          rows={10}
          placeholder={
            sets.length === 0 ? 'Write the email.' : 'Write the email, or pick a copy set above.'
          }
          onPhoto={photoPicker.pick}
          photoPreview={photoPicker.previewFor}
        />
        {photoPicker.PhotoInput}
        {!sender ? null : (
          <p className="tracker-field-note">
            Goes out from Alex&apos;s mailbox with his Gmail signature. Recorded on this lead as
            sent by you.
          </p>
        )}
        {!held ? null : (
          <p className="tracker-field-note">
            {holdLabel(lead)}
            {lead.email_hold_note.trim() === '' ? '' : `: ${lead.email_hold_note}`}. New sends are
            refused until the hold clears.
          </p>
        )}

        <div className="lead-send-actions">
          {!sender ? null : (
            <Button size="sm" onClick={sendNow} disabled={send.isPending || compose.isPending}>
              {send.isPending ? 'Sending…' : 'Send as Alex'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={create}
            disabled={compose.isPending || send.isPending}
          >
            {compose.isPending
              ? 'Creating…'
              : sender
                ? 'Gmail draft instead'
                : 'Create Gmail draft'}
          </Button>
        </div>
        {message !== '' && (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            {message}
          </p>
        )}
        {!drafted ? null : (
          <div className="tracker-field-block">
            <Button
              variant="secondary"
              size="sm"
              disabled={logSent.isPending}
              onClick={() => {
                logSent.mutate(
                  { leadId: lead.id, kind: 'email_sent', body: `Email ${String(touch)}` },
                  {
                    onSuccess: () => {
                      setDrafted(false);
                      setMessage('Logged. It is on the lead’s Activity now.');
                    },
                    onError: (cause) => setMessage(activityErrorMessage(cause.message)),
                  },
                );
              }}
            >
              {logSent.isPending ? 'Logging…' : 'Mark email sent'}
            </Button>
            <p className="tracker-field-note">
              Press this once you have actually sent it in Gmail. Creating a draft is not sending
              one, so nothing is recorded until you say so.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Outreach view (was Lists) ─────────────────────────────────────────────────
